/** Sessões partilhadas: as regras da interface, sem React nem rede.
 *
 *  "Partilhar sessão" partilha de uma vez o que hoje se grava num
 *  ficheiro .vn2r-session: os modelos abertos, a triagem dos conflitos,
 *  os BCF e os pontos de vista. No servidor fica um MANIFESTO (o que
 *  abrir e como repor a vista) que referencia as peças pelas partilhas
 *  delas; os ficheiros IFC nunca vão dentro dele (sobem pelo caminho
 *  dos modelos partilhados e abrem-se pelo SHA-256, como na sessão
 *  leve). Os pontos de vista também vão à parte: num pacote comprimido,
 *  em blocos, que o manifesto só referencia (no Cha139 são 1191, com
 *  o .bcfv original e a miniatura de cada um: não cabiam no manifesto).
 *  Ver docs/PARTILHA.md, "Sessões".
 */
import type { VnSerialViewpoint, VnSessionLocalWork, VnSessionModel } from '@core/session/session'
import type { PartilhaInfo } from '@core/partilhas/partilhasRegras'
import type { GeoPlacement } from '@viewer/lib/geoTransform'
import { DIAS_NA_RECICLAGEM } from '@core/modelos/reciclagemRegras'

export const VERSAO_DO_MANIFESTO = 1

/** O pacote dos pontos de vista: blocos de 4 MB, no máximo 16 (os
 *  limites do servidor, `BLOCO_DA_SESSAO_BYTES` e
 *  `MAXIMO_BLOCOS_DA_SESSAO`). */
export const BLOCO_DA_SESSAO = 4 * 1024 * 1024
export const MAXIMO_BLOCOS_DA_SESSAO = 16

/** O pacote dos pontos de vista, como o manifesto o referencia. O
 *  SHA-256 é do pacote comprimido, para quem abre confirmar que chegou
 *  inteiro (e não a meio de um envio). */
export interface PacoteDosPontosDeVista {
  blocos: number
  bytes: number
  sha256: string
  quantos: number
  sem_miniaturas: boolean
}

/** As chaves do trabalho local que a sessão partilhada leva: as vistas
 *  2D (cortes, anotações, cotas) e os planos de corte do 3D, com as
 *  combinações e os estilos. Os rascunhos IDS são pessoais. */
const PREFIXO_VISTAS_2D = 'vn2r.views2d.'
const CHAVE_DOS_CORTES = 'vn2r.cortes'

export interface ModeloDaSessao {
  nome: string
  sha256: string
  schema_version: string | null
  placement_override?: GeoPlacement | null
  /** A partilha do modelo (null se não pôde subir: fica de fora). */
  partilha_id: string | null
}

export interface TriagemDaSessao {
  projeto_id: string
  codigo: string
  partilha_id: string | null
}

export interface BcfDaSessao {
  partilha_id: string
  /** O identificador de projecto do ficheiro, para exportar com ele. */
  project_id: string | null
  nome: string
}

export interface ManifestoDaSessao {
  versao: number
  nome: string
  guardada_em: string
  layout_mode: string | null
  modelos: ModeloDaSessao[]
  modelo_ativo_sha256: string | null
  triagem: TriagemDaSessao | null
  bcfs: BcfDaSessao[]
  bcf_ativo_partilha_id: string | null
  /** Os pontos de vista, no pacote à parte (null: sem pontos de vista). */
  pontos_de_vista: PacoteDosPontosDeVista | null
  /** Vazio nas sessões novas: os pontos de vista vão no pacote. */
  viewpoints: VnSerialViewpoint[]
  local_work: VnSessionLocalWork[]
}

export function construirManifesto(
  o: Omit<ManifestoDaSessao, 'versao' | 'guardada_em' | 'local_work' | 'viewpoints' | 'pontos_de_vista'> & {
    local_work: readonly VnSessionLocalWork[]
    pontos_de_vista?: PacoteDosPontosDeVista | null
    guardada_em?: string
  },
): ManifestoDaSessao {
  return {
    versao: VERSAO_DO_MANIFESTO,
    nome: o.nome,
    guardada_em: o.guardada_em ?? new Date().toISOString(),
    layout_mode: o.layout_mode,
    modelos: o.modelos,
    modelo_ativo_sha256: o.modelo_ativo_sha256,
    triagem: o.triagem,
    bcfs: o.bcfs,
    bcf_ativo_partilha_id: o.bcf_ativo_partilha_id,
    pontos_de_vista: o.pontos_de_vista ?? null,
    viewpoints: [],
    local_work: vistasDoTrabalho(o.local_work),
  }
}

/** Só o trabalho local que se partilha: as vistas 2D e os cortes. */
export function vistasDoTrabalho(itens: readonly VnSessionLocalWork[]): VnSessionLocalWork[] {
  return itens.filter((i) => i.key.startsWith(PREFIXO_VISTAS_2D) || i.key === CHAVE_DOS_CORTES)
}

/** As partilhas que o manifesto referencia, sem repetidos: são as peças
 *  da sessão no servidor. */
export function pecasDoManifesto(m: ManifestoDaSessao): string[] {
  const ids = [
    ...m.modelos.map((x) => x.partilha_id),
    m.triagem?.partilha_id ?? null,
    ...m.bcfs.map((b) => b.partilha_id),
  ].filter((x): x is string => !!x)
  return [...new Set(ids)]
}

/** Quantos blocos leva um pacote com estes bytes. */
export function blocosDoPacote(bytes: number, bloco: number = BLOCO_DA_SESSAO): number {
  return bytes <= 0 ? 0 : Math.ceil(bytes / bloco)
}

/** O pacote cabe no servidor? */
export function pacoteCabe(bytes: number, maximo: number = MAXIMO_BLOCOS_DA_SESSAO): boolean {
  return blocosDoPacote(bytes) <= maximo
}

/** Os mesmos pontos de vista, sem as miniaturas (o último recurso para
 *  o pacote caber: a câmara, os elementos e o .bcfv original ficam). */
export function semMiniaturas(lista: readonly VnSerialViewpoint[]): VnSerialViewpoint[] {
  return lista.map((v) => ({ ...v, thumbnail_data_url: null, thumbnail_mime: null }))
}

/** Lê o pacote dos pontos de vista depois de descomprimido, sem confiar
 *  na forma: o que não for um ponto de vista fica de fora. */
export function lerPacote(raw: unknown): VnSerialViewpoint[] {
  if (!Array.isArray(raw)) return []
  return raw.filter(
    (v): v is VnSerialViewpoint =>
      !!v && typeof v === 'object' && !!(v as { record?: unknown }).record
      && typeof (v as { record?: unknown }).record === 'object',
  )
}

function lerPacoteDoManifesto(v: unknown): PacoteDosPontosDeVista | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const inteiro = (x: unknown) => (typeof x === 'number' && Number.isInteger(x) && x >= 0 ? x : null)
  const blocos = inteiro(o.blocos)
  const bytes = inteiro(o.bytes)
  const sha = typeof o.sha256 === 'string' && /^[0-9a-f]{64}$/i.test(o.sha256) ? o.sha256.toLowerCase() : null
  if (blocos === null || blocos === 0 || bytes === null || !sha) return null
  return {
    blocos,
    bytes,
    sha256: sha,
    quantos: inteiro(o.quantos) ?? 0,
    sem_miniaturas: o.sem_miniaturas === true,
  }
}

function texto(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}

/** Lê o manifesto que veio do servidor, sem confiar na forma: o que não
 *  servir fica de fora em vez de partir a abertura. */
export function lerManifesto(raw: unknown): ManifestoDaSessao | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  if (typeof o.versao !== 'number' || o.versao > VERSAO_DO_MANIFESTO) return null
  const lista = (v: unknown): Record<string, unknown>[] =>
    Array.isArray(v) ? v.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object') : []
  const modelos: ModeloDaSessao[] = lista(o.modelos)
    .filter((x) => texto(x.sha256) && texto(x.nome))
    .map((x) => ({
      nome: String(x.nome),
      sha256: String(x.sha256).toLowerCase(),
      schema_version: texto(x.schema_version),
      placement_override:
        x.placement_override && typeof x.placement_override === 'object'
          ? (x.placement_override as GeoPlacement)
          : null,
      partilha_id: texto(x.partilha_id),
    }))
  const t = o.triagem && typeof o.triagem === 'object' ? (o.triagem as Record<string, unknown>) : null
  const triagem: TriagemDaSessao | null =
    t && texto(t.projeto_id)
      ? { projeto_id: String(t.projeto_id), codigo: texto(t.codigo) ?? 'Triagem', partilha_id: texto(t.partilha_id) }
      : null
  const bcfs: BcfDaSessao[] = lista(o.bcfs)
    .filter((b) => texto(b.partilha_id))
    .map((b) => ({ partilha_id: String(b.partilha_id), project_id: texto(b.project_id), nome: texto(b.nome) ?? 'BCF' }))
  const viewpoints = lista(o.viewpoints).filter(
    (v) => v.record && typeof v.record === 'object',
  ) as unknown as VnSerialViewpoint[]
  const localWork = lista(o.local_work)
    .filter((w) => texto(w.key) && typeof w.raw === 'string')
    .map((w) => ({ key: String(w.key), raw: String(w.raw) }))
  return {
    versao: o.versao,
    nome: texto(o.nome) ?? 'Sessão',
    guardada_em: texto(o.guardada_em) ?? '',
    layout_mode: texto(o.layout_mode),
    modelos,
    modelo_ativo_sha256: texto(o.modelo_ativo_sha256),
    triagem,
    bcfs,
    bcf_ativo_partilha_id: texto(o.bcf_ativo_partilha_id),
    pontos_de_vista: lerPacoteDoManifesto(o.pontos_de_vista),
    viewpoints,
    local_work: vistasDoTrabalho(localWork),
  }
}

/** O nome que a janela sugere: o código da triagem (é como a equipa
 *  chama ao trabalho, "Cha139"), senão o primeiro modelo sem extensão,
 *  senão "Sessão de <data>". */
export function nomeSugerido(o: {
  triagem: string | null
  modelos: readonly string[]
  data?: Date
}): string {
  if (o.triagem) return o.triagem
  const primeiro = o.modelos[0]
  if (primeiro) return primeiro.replace(/\.(ifc|ifczip|ifcxml|frag)$/i, '')
  const d = o.data ?? new Date()
  return `Sessão de ${d.toLocaleDateString('pt-PT')}`
}

/** Uma peça como o servidor a devolve em `GET /api/sessoes/{id}`. */
export interface PecaDaSessao {
  id: string
  tipo: 'modelo' | 'bcf' | 'triagem'
  nome: string | null
  chave: string
  recurso: string
  acesso: boolean
  /** Falso enquanto o dono (outra pessoa) não autorizar. */
  autorizada?: boolean
  dono_nome?: string | null
}

/** "à espera da autorização de Ana", se a peça espera; senão null. */
function esperaDe(peca: PecaDaSessao | undefined): string | null {
  if (!peca || peca.autorizada !== false || peca.acesso) return null
  return `espera a autorização de ${peca.dono_nome || 'quem o partilhou'}`
}

export interface PlanoDeAbertura {
  /** Os modelos a repor (na ordem do manifesto), com a peça de onde vêm. */
  modelos: { modelo: ModeloDaSessao; peca: PecaDaSessao }[]
  triagem: { triagem: TriagemDaSessao; peca: PecaDaSessao } | null
  bcfs: { bcf: BcfDaSessao; peca: PecaDaSessao }[]
  /** O que fica de fora, dito a quem abre. */
  avisos: string[]
}

/** O que abrir, cruzando o manifesto com as peças a que quem abre tem
 *  acesso. Uma peça sem acesso (o dono deixou de a partilhar, ou nunca
 *  pôde entrar na sessão) fica de fora, com aviso. */
export function planoDeAbertura(m: ManifestoDaSessao, pecas: readonly PecaDaSessao[]): PlanoDeAbertura {
  const porId = new Map(pecas.map((p) => [p.id, p]))
  const avisos: string[] = []
  const modelos: PlanoDeAbertura['modelos'] = []
  for (const modelo of m.modelos) {
    const peca = modelo.partilha_id ? porId.get(modelo.partilha_id) : undefined
    if (!peca || peca.tipo !== 'modelo' || !peca.acesso) {
      const espera = esperaDe(peca)
      avisos.push(
        espera
          ? `O modelo "${modelo.nome}" ${espera}: ficou de fora por agora.`
          : `O modelo "${modelo.nome}" não está partilhado consigo: ficou de fora.`,
      )
      continue
    }
    modelos.push({ modelo, peca })
  }
  let triagem: PlanoDeAbertura['triagem'] = null
  if (m.triagem) {
    const peca = m.triagem.partilha_id ? porId.get(m.triagem.partilha_id) : undefined
    if (peca && peca.tipo === 'triagem' && peca.acesso) triagem = { triagem: m.triagem, peca }
    else {
      const espera = esperaDe(peca)
      avisos.push(
        espera
          ? `A triagem dos conflitos "${m.triagem.codigo}" ${espera}: ficou de fora por agora.`
          : `A triagem dos conflitos "${m.triagem.codigo}" não está partilhada consigo: ficou de fora.`,
      )
    }
  }
  const bcfs: PlanoDeAbertura['bcfs'] = []
  for (const bcf of m.bcfs) {
    if (bcfs.some((b) => b.bcf.partilha_id === bcf.partilha_id)) continue
    const peca = porId.get(bcf.partilha_id)
    if (!peca || peca.tipo !== 'bcf' || !peca.acesso) {
      const espera = esperaDe(peca)
      avisos.push(
        espera
          ? `O BCF "${bcf.nome}" ${espera}: ficou de fora por agora.`
          : `O BCF "${bcf.nome}" não está partilhado consigo: ficou de fora.`,
      )
      continue
    }
    bcfs.push({ bcf, peca })
  }
  return { modelos, triagem, bcfs, avisos }
}

/** As partilhas que o manifesto refere e que não vieram com a sessão
 *  (ou vieram sem acesso). Quem abre pode tê-las por si: um BCF que foi
 *  ele próprio a registar, por exemplo, não entra nas peças da sessão
 *  (só o dono o partilha), mas abre-se na mesma. */
export function pecasPorConfirmar(m: ManifestoDaSessao, pecas: readonly PecaDaSessao[]): string[] {
  const comAcesso = new Set(pecas.filter((p) => p.acesso).map((p) => p.id))
  return pecasDoManifesto(m).filter((id) => !comAcesso.has(id))
}

/** Uma partilha a que quem abre tem acesso por si, como peça da sessão
 *  (o `recurso` como o servidor o dá, em `core/partilhas.py`). */
export function pecaDaPartilha(info: PartilhaInfo): PecaDaSessao | null {
  if (info.tipo !== 'modelo' && info.tipo !== 'bcf' && info.tipo !== 'triagem') return null
  return {
    id: info.id,
    tipo: info.tipo,
    nome: info.nome,
    chave: info.chave,
    recurso: info.tipo === 'triagem' ? info.chave : info.tipo === 'bcf' ? `partilha:${info.id}` : info.id,
    acesso: true,
  }
}

/** Os modelos para a reposição (o banner da sessão e o viewer usam esta
 *  forma, com a posição manual de cada um). */
export function modelosParaReposicao(plano: PlanoDeAbertura): VnSessionModel[] {
  return plano.modelos.map(({ modelo }) => ({
    filename: modelo.nome,
    sha256: modelo.sha256,
    schema_version: modelo.schema_version,
    placement_override: modelo.placement_override ?? null,
    source_format: 'ifc' as const,
    partilhado: true,
  }))
}

/** A pergunta antes de abrir uma sessão partilhada por cima do que está
 *  aberto (abrir uma sessão substitui a actual, como abrir um ficheiro). */
export function textoDeSubstituir(nome: string, modelos: number, bcfs: number): string | null {
  if (modelos === 0 && bcfs === 0) return null
  const partes: string[] = []
  if (modelos) partes.push(`${modelos} modelo${modelos === 1 ? '' : 's'}`)
  if (bcfs) partes.push(`${bcfs} BCF`)
  return (
    `Abrir a sessão partilhada "${nome}" fecha a sessão atual (${partes.join(' e ')} aberto${modelos + bcfs === 1 ? '' : 's'}). `
    + 'O que não foi gravado com "Guardar sessão" perde-se. Continuar?'
  )
}

/** O aviso quando, ao abrir uma sessão partilhada, a pessoa escolheu
 *  recuperar o trabalho anterior: a sessão não abre por cima dele. */
export function textoDeSessaoAdiada(nome: string): string {
  return (
    `Recuperou o trabalho anterior, por isso a sessão partilhada "${nome}" não foi aberta por cima dele. `
    + 'Grave-o com "Guardar sessão" se o quiser manter, e abra a sessão de novo pela ligação ou em "Partilhados comigo".'
  )
}

/** A pergunta antes de apagar uma sessão partilhada. */
export function textoDeApagarSessao(nome: string, bytes: number, mb: (n: number) => string): string {
  const espaco = bytes > 0 ? ` Liberta ${mb(bytes)} do espaço do workspace.` : ''
  return (
    `Apagar a sessão partilhada "${nome}"? Vai para a Reciclagem, onde pode ser restaurada durante `
    + `${DIAS_NA_RECICLAGEM} dias; depois é apagada de vez. Quem a recebeu deixa logo de a abrir e `
    + `perde os acessos que ela lhe dava.${espaco} Os modelos, a triagem e os BCF ficam como estavam, `
    + 'com os acessos que já tinham por si.'
  )
}

const COISA_DO_TIPO = { modelo: 'o modelo', bcf: 'o BCF', triagem: 'a triagem dos conflitos' } as const

/** O texto de um pedido de autorização, para quem é dono da peça. */
export function textoDoPedido(p: {
  sessao_nome: string | null
  pedido_por: string | null
  peca_tipo: 'modelo' | 'bcf' | 'triagem'
  peca_nome: string | null
  pessoas: readonly string[]
}): string {
  const quem = p.pedido_por || 'Alguém'
  const coisa = `${COISA_DO_TIPO[p.peca_tipo]}${p.peca_nome ? ` "${p.peca_nome}"` : ''}`
  const para = p.pessoas.length
    ? `Se autorizar, passam a ter acesso: ${p.pessoas.join(', ')}.`
    : `A sessão ainda não está partilhada com ninguém: quem ${quem} acrescentar depois passa a ter acesso.`
  return `${quem} incluiu ${coisa} na sessão "${p.sessao_nome || 'sem nome'}". ${para}`
}

/** O resumo do que abriu, para o aviso final. */
export function textoDoResultado(nome: string, plano: PlanoDeAbertura): string {
  const partes: string[] = []
  if (plano.modelos.length) partes.push(`${plano.modelos.length} modelo${plano.modelos.length === 1 ? '' : 's'}`)
  if (plano.triagem) partes.push(`a triagem "${plano.triagem.triagem.codigo}"`)
  if (plano.bcfs.length) partes.push(`${plano.bcfs.length} BCF`)
  const inclui = partes.length > 1 ? `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}` : partes[0]
  return inclui ? `Sessão "${nome}" aberta: ${inclui}.` : `Sessão "${nome}" aberta.`
}

/** Uma chave nova para uma sessão (o servidor identifica-a por ela:
 *  partilhar outra vez a mesma sessão actualiza-a). */
export function chaveNova(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}
